import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Column, PageSizeType, Table } from 'design-system-web';
import { Alert } from 'components/lds';

import { buildCoverageHoursRowActions } from './coverageHoursRowActions';
import { formatCoverageHours } from './coverageHoursRowFormat';
import {
  COVERAGE_HOURS_PAGE_SIZE,
  CoverageHoursEntry,
  useCoverageHours,
} from './useCoverageHours';

type Props = {
  channelId: string;
  canManage: boolean;
};

// The server hashes every grouping field into the id, including the timezone.
// A key built from the displayed fields cannot separate two entries that differ
// only by zone.
export const coverageHoursRowId = (row: CoverageHoursEntry): string => row.id;

export function CoverageHoursTable({ channelId, canManage }: Props) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<PageSizeType>(
    COVERAGE_HOURS_PAGE_SIZE
  );
  const { entries, totalRows, loading, error, fetchCoverageHours } =
    useCoverageHours({
      channelId,
      pageIndex: page,
      perPage: pageSize,
    });

  useEffect(() => {
    fetchCoverageHours();
  }, [fetchCoverageHours]);

  const handlePageSizeChange = (nextPageSize: PageSizeType) => {
    setPageSize(nextPageSize);
    setPage(0);
  };

  const columns: Column<CoverageHoursEntry>[] = useMemo(
    () => [
      {
        key: 'categoryIssue',
        header: t(
          'web.admin.serviceRequest.settings.coverageHours.table.categoryIssue'
        ),
        renderCell: (_cell: unknown, row: CoverageHoursEntry) =>
          `${row.category} – ${
            row.issue ??
            t('web.admin.serviceRequest.settings.coverageHours.create.none')
          }`,
      },
      {
        key: 'team',
        header: t('web.admin.serviceRequest.settings.coverageHours.table.team'),
        renderCell: (_cell: unknown, row: CoverageHoursEntry) =>
          row.teamName ??
          t(
            'web.admin.serviceRequest.settings.coverageHours.table.teamUnavailable'
          ),
      },
      {
        key: 'coverageHours',
        header: t(
          'web.admin.serviceRequest.settings.coverageHours.table.coverageHours'
        ),
        renderCell: (_cell: unknown, row: CoverageHoursEntry) =>
          formatCoverageHours(row),
      },
    ],
    [t]
  );

  const rowActions = useMemo(
    () => (canManage ? buildCoverageHoursRowActions(t) : undefined),
    [canManage, t]
  );

  if (error) {
    return (
      <Alert type="error">
        {t('web.admin.serviceRequest.settings.coverageHours.error.load')}
      </Alert>
    );
  }

  return (
    <Table<CoverageHoursEntry>
      columns={columns}
      data={entries}
      rowActions={rowActions}
      disableSorting
      isLoading={loading}
      getRowId={coverageHoursRowId}
      pagination="server"
      totalRows={totalRows}
      page={page}
      pageSize={pageSize}
      onPageChange={setPage}
      onPageSizeChange={handlePageSizeChange}
      showColumnVisibility
      tableKey="serviceRequestSettings.coverageHoursTable"
      emptyMessage={t(
        canManage
          ? 'web.admin.serviceRequest.settings.coverageHours.empty'
          : 'web.admin.serviceRequest.settings.coverageHours.emptyReadOnly'
      )}
    />
  );
}
